import { useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { useCartStore } from '@/store/cartStore'
import { subscribeToCart, addToCart, updateCartItemQuantity, removeFromCart, clearCart } from '@/services/api/cart'
import { useUIStore } from '@/store/uiStore'

/**
 * Hook that manages cart subscription and provides cart actions.
 * Call once at app level (e.g., App.tsx) to set up the real-time subscription.
 */
export function useCartSubscription() {
  const user = useAuthStore((s) => s.user)
  const setItems = useCartStore((s) => s.setItems)
  const setLoading = useCartStore((s) => s.setLoading)
  const setError = useCartStore((s) => s.setError)

  useEffect(() => {
    if (!user) {
      setItems([])
      return
    }

    setLoading(true)
    const unsub = subscribeToCart(
      user.uid,
      (items) => setItems(items),
      (error) => {
        setError(error.message || 'Failed to load cart')
        setItems([])
      }
    )

    return unsub
  }, [user?.uid])
}

/**
 * Hook for components that need cart actions (add, update, remove).
 */
export function useCartActions() {
  const user = useAuthStore((s) => s.user)
  const { showToast } = useUIStore()
  const openDrawer = useCartStore((s) => s.openDrawer)

  const add = async (item: {
    listingId: string
    sellerId: string
    price: number
    title: string
    image: string
    quantity?: number
  }) => {
    if (!user) return
    try {
      await addToCart(user.uid, item)
      showToast('Added to cart')
      openDrawer()
    } catch (err: any) {
      showToast(err.message || 'Failed to add to cart', 'error')
    }
  }

  const updateQuantity = async (itemId: string, quantity: number) => {
    if (!user) return
    try {
      await updateCartItemQuantity(user.uid, itemId, quantity)
    } catch {
      showToast('Failed to update quantity', 'error')
    }
  }

  const remove = async (itemId: string) => {
    if (!user) return
    try {
      await removeFromCart(user.uid, itemId)
      showToast('Removed from cart')
    } catch {
      showToast('Failed to remove item', 'error')
    }
  }

  const clear = async () => {
    if (!user) return
    try {
      await clearCart(user.uid)
    } catch {
      showToast('Failed to clear cart', 'error')
    }
  }

  return { add, updateQuantity, remove, clear }
}
